// components/PendienteModal.js
import React from 'react';
import { 
  LuClock, 
  LuFlag, 
  LuCalendar, 
  LuTimer, 
  LuX, 
  LuSearch, 
  LuTrash2, 
  LuRefreshCw,
  LuShieldCheck,
  LuQrCode,
  LuCircleCheck,
  LuCircleAlert,
  LuListOrdered
} from 'react-icons/lu';
import './PendienteModal.css';

const PendienteModal = ({ 
    data, 
    onVerificar, 
    onCancelar, 
    onClose, 
    isLoading = false,
    mensaje = null,
    cooldown = 0,
    intentosMaximos = false
}) => {
    // Validación robusta de fecha
    const fechaCreacion = data?.fechaCreacion ? new Date(data.fechaCreacion) : null;
    const fechaValida = fechaCreacion && !isNaN(fechaCreacion.getTime());
    
    const getTiempoTranscurrido = () => {
        if (!fechaValida) return "Fecha desconocida";
        const diffMs = Date.now() - fechaCreacion.getTime();
        const diffMin = Math.floor(diffMs / (1000 * 60));
        const diffHoras = Math.floor(diffMin / 60);
        const diffDias = Math.floor(diffHoras / 24);
        
        if (diffDias > 0) return `${diffDias} día${diffDias > 1 ? 's' : ''}`;
        if (diffHoras > 0) return `${diffHoras} hora${diffHoras > 1 ? 's' : ''}`;
        if (diffMin > 0) return `${diffMin} minuto${diffMin > 1 ? 's' : ''}`;
        return "Hace un momento";
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !isLoading && mensaje?.type !== 'success') {
            onClose?.();
        }
    };

    // 🏆 VISTA DE PAGO EXITOSO / CONFIRMADO
    if (mensaje?.type === 'success') {
        return (
            <div className="pendiente-modal-overlay">
                <div className="pendiente-modal-content pendiente-modal-success">
                    {/* Icono Neón Verde */}
                    <div className="pendiente-modal-header-icon success-glow">
                        <LuCircleCheck className="pendiente-icon-success" />
                    </div>

                    <h3 className="pendiente-modal-title">¡Listo! Pago confirmado</h3>
                    
                    <p className="pendiente-modal-description">
                        Recibimos tu transferencia para <strong className="event-highlight">{data?.competenciaSlug || 'este evento'}</strong>. Ya estás oficialmente dentro de la carrera.
                    </p>

                    <div className="pendiente-success-box">
                        <p className="success-main-text">
                            <LuShieldCheck className="inline-icon" /> Tu cupo está 100% asegurado
                        </p>
                        <p className="subtext">
                            Ya puedes revisar tu nombre en la lista de competidores inscritos.
                        </p>
                    </div>

                    <div className="pendiente-buttons-container" style={{ marginTop: '20px' }}>
                        <button 
                            className="pendiente-btn-verificar success-btn"
                            onClick={() => window.location.reload()}
                            type="button"
                        >
                            <LuListOrdered className="btn-icon" /> Ver lista de inscritos
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pendiente-modal-overlay" onClick={handleOverlayClick}>
            <div className="pendiente-modal-content" onClick={(e) => e.stopPropagation()}>
                {!isLoading && (
                    <button 
                        className="pendiente-modal-close-btn" 
                        onClick={onClose}
                        type="button"
                        aria-label="Cerrar"
                    >
                        <LuX />
                    </button>
                )}
                
                {/* Icono Principal Glow Naranja */}
                <div className="pendiente-modal-header-icon">
                    <LuClock className="pendiente-icon-glow" />
                </div>

                <h3 className="pendiente-modal-title">¿Ya realizaste tu pago por QR?</h3>
                
                <p className="pendiente-modal-description">
                    Tienes una transacción iniciada. Si ya escaneaste y transferiste desde tu banco, confirma aquí tu pago.
                </p>
                
                {/* Alerta si falla o aún no registra la transferencia */}
                {mensaje && mensaje.type !== 'success' && (
                    <div className={`pendiente-alert-banner ${mensaje.type}`}>
                        <LuCircleAlert className="alert-icon" />
                        <span>{mensaje.text.replace(/⚠️|✅/g, '')}</span> {/* Limpia cualquier emoji residual */}
                    </div>
                )}
                
                {/* Tarjeta de Información del Pago */}
                <div className="pendiente-info-container">
                    <div className="pendiente-info-row">
                        <span className="pendiente-info-label">
                            <LuFlag className="info-icon" /> Evento / Carrera
                        </span>
                        <span className="pendiente-info-value">{data?.competenciaSlug || 'No especificada'}</span>
                    </div>

                    <div className="pendiente-info-row">
                        <span className="pendiente-info-label">
                            <LuCalendar className="info-icon" /> Registro
                        </span>
                        <span className="pendiente-info-value">
                            {fechaValida ? fechaCreacion.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : 'No disponible'}
                        </span>
                    </div>

                    <div className="pendiente-info-row">
                        <span className="pendiente-info-label">
                            <LuTimer className="info-icon" /> Transcurrido
                        </span>
                        <span className="pendiente-info-value highlight">{getTiempoTranscurrido()}</span>
                    </div>
                </div>
                
                <p className="pendiente-modal-question">Presiona el botón para consultar tu transferencia:</p>
                
                {/* Botones de Acción */}
                <div className="pendiente-buttons-container">
                <button 
                        className="pendiente-btn-verificar"
                        onClick={onVerificar}
                        disabled={isLoading || cooldown > 0 || intentosMaximos}
                        type="button"
                    >
                        {isLoading ? (
                            <>
                                <LuRefreshCw className="spinner-icon" /> Verificando...
                            </>
                        ) : cooldown > 0 ? (
                            <>
                                <LuClock className="btn-icon" /> Espera {cooldown}s para reintentar
                            </>
                        ) : intentosMaximos ? (
                            <>
                                <LuCircleAlert className="btn-icon" /> Límite de intentos alcanzado
                            </>
                        ) : (
                            <>
                                <LuSearch className="btn-icon" /> Sí, ya pagué / Verificar
                            </>
                        )}
                    </button>

                    <button 
                        className="pendiente-btn-cancelar"
                        onClick={onCancelar}
                        disabled={isLoading}
                        type="button"
                    >
                        <LuTrash2 className="btn-icon" /> No pagué, descartar
                    </button>
                </div>

                {/* Footer Seguridad */}
                <div className="pendiente-security-footer">
                    <span className="security-item">
                        <LuShieldCheck className="security-icon" /> Verificación segura 24/7
                    </span>
                    <span className="security-bullet">•</span>
                    <span className="security-item">
                        <LuQrCode className="security-icon" /> Pago QR Bancario
                    </span>
                </div>
            </div>
        </div>
    );
};

export default PendienteModal;